/*
 * Creative Library delivery signal — the shared reading of the
 * `/api/creative-assets/live-delivery` payload for the Creative Assets page.
 *
 * The payload carries one entry per Cphere asset with its platform
 * CONTRIBUTIONS (StackAdapt CTV/Display at ad grain; YouTube from the retained
 * Google Ads report rows through the verified identity mapping registry),
 * each summed once, plus a backend-derived `primaryKpi` stated in the leading
 * platform's own vocabulary (VVR for YouTube, VCR for CTV, CTR for Display).
 *
 * Two states this module keeps apart, because the page used to blur them:
 *   - AWAITING DELIVERY: nothing has been reported for the asset;
 *   - DELIVERED, NO RATE: spend / impressions were reported but the source
 *     carries no numerator or denominator for a rate (e.g. a CTV row with no
 *     video readings). That is delivery, and must never read as awaiting.
 *
 * Nothing here says "live": delivery is historical evidence and implies
 * nothing about a campaign's lifecycle, a creative's approval, or whether
 * anything is running now.
 *
 * This file is byte-identical between Cortex and Quantum.
 */

/*
 * First finite reading, or null. An explicit null / undefined / empty value
 * is "not reported" and is skipped — never coerced to 0, because a zero is a
 * reading and an absence is not (a CTV row with no video readings must not
 * render "0 views").
 */
export function pickNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

const KPI_LABELS = Object.freeze({
  videoViewRate: "VVR",
  completionRate: "VCR",
  ctr: "CTR",
});

function normalizeContribution(entry) {
  if (!entry || typeof entry !== "object") return null;
  const rates = entry.rates && typeof entry.rates === "object" ? entry.rates : {};
  return {
    platformKey: String(entry.platformKey || "").trim() || null,
    campaignId: entry.campaignId ?? null,
    campaignName: String(entry.campaignName || "").trim() || null,
    sourcePlatform: String(entry.sourcePlatform || "").trim() || null,
    sourceLabel: String(entry.sourceLabel || "").trim() || null,
    spend: pickNumber(entry.billedSpend, entry.clientFacingSpend, entry.spend),
    impressions: pickNumber(entry.impressions),
    clicks: pickNumber(entry.clicks),
    videoViews: pickNumber(entry.videoViews),
    videoStarts: pickNumber(entry.videoStarts),
    videoCompletions: pickNumber(entry.videoCompletions),
    rows: pickNumber(entry.rows),
    firstDate: entry.firstDate || null,
    lastDate: entry.lastDate || null,
    lastSyncedAt: entry.lastSyncedAt || null,
    rates: {
      ctr: pickNumber(rates.ctr),
      videoViewRate: pickNumber(rates.videoViewRate),
      completionRate: pickNumber(rates.completionRate),
      cpm: pickNumber(rates.cpm),
    },
  };
}

function normalizePrimaryKpi(value) {
  if (!value || typeof value !== "object") return null;
  const key = String(value.key || "").trim();
  const numeric = pickNumber(value.value);
  if (!key || numeric == null) return null;
  return {
    key,
    label: String(value.label || KPI_LABELS[key] || key).trim(),
    value: numeric,
    platformKey: String(value.platformKey || "").trim() || null,
    campaignId: value.campaignId ?? null,
  };
}

export function normalizeCreativeLiveDeliveryMap(payload) {
  const root =
    payload?.data && typeof payload.data === "object"
      ? payload.data
      : payload && typeof payload === "object"
      ? payload
      : {};
  const metricsByAssetId =
    root?.metricsByAssetId && typeof root.metricsByAssetId === "object"
      ? root.metricsByAssetId
      : {};

  return Object.fromEntries(
    Object.entries(metricsByAssetId).map(([assetId, value]) => [
      String(assetId),
      {
        ...value,
        spend: pickNumber(
          value?.billedSpend,
          value?.clientFacingSpend,
          value?.effectiveSpendRaw,
          value?.spend
        ),
        billedSpend: pickNumber(
          value?.billedSpend,
          value?.clientFacingSpend,
          value?.effectiveSpendRaw
        ),
        providerSpend: pickNumber(value?.providerSpend),
        impressions: pickNumber(value?.impressions),
        clicks: pickNumber(value?.clicks),
        videoViews: pickNumber(value?.videoViews),
        videoStarts: pickNumber(value?.videoStarts),
        videoCompletions: pickNumber(value?.videoCompletions),
        pricingMatched: Boolean(value?.pricingMatched),
        pricingComplete: Boolean(value?.pricingComplete),
        pricingModel: String(value?.pricingModel || "").trim() || null,
        sourcePlatform: String(value?.sourcePlatform || "").trim() || null,
        sourcePlatforms: Array.isArray(value?.sourcePlatforms)
          ? value.sourcePlatforms.map((entry) => String(entry || "").trim()).filter(Boolean)
          : [],
        platformKeys: Array.isArray(value?.platformKeys)
          ? value.platformKeys.map((entry) => String(entry || "").trim()).filter(Boolean)
          : [],
        contributions: Array.isArray(value?.contributions)
          ? value.contributions.map(normalizeContribution).filter(Boolean)
          : [],
        primaryKpi: normalizePrimaryKpi(value?.primaryKpi),
        deliveryObserved: Boolean(value?.deliveryObserved),
        lastSyncedAt: value?.lastSyncedAt || null,
      },
    ])
  );
}

export function mergeCreativeMetrics(baseMetrics, liveMetrics) {
  const localMetrics = baseMetrics || {};
  if (!liveMetrics || typeof liveMetrics !== "object") return localMetrics;

  return {
    ...localMetrics,
    spend: pickNumber(
      liveMetrics?.billedSpend,
      liveMetrics?.clientFacingSpend,
      liveMetrics?.effectiveSpendRaw,
      liveMetrics?.spend
    ),
    billedSpend: pickNumber(
      liveMetrics?.billedSpend,
      liveMetrics?.clientFacingSpend,
      liveMetrics?.effectiveSpendRaw,
      liveMetrics?.spend,
      localMetrics?.billedSpend
    ),
    providerSpend: pickNumber(liveMetrics?.providerSpend, localMetrics?.providerSpend),
    impressions: pickNumber(liveMetrics?.impressions, localMetrics?.impressions),
    clicks: pickNumber(liveMetrics?.clicks, localMetrics?.clicks),
    videoViews: pickNumber(liveMetrics?.videoViews, localMetrics?.videoViews),
    videoStarts: pickNumber(liveMetrics?.videoStarts, localMetrics?.videoStarts),
    videoCompletions: pickNumber(liveMetrics?.videoCompletions, localMetrics?.videoCompletions),
    pricingMatched:
      typeof liveMetrics?.pricingMatched === "boolean"
        ? liveMetrics.pricingMatched
        : localMetrics?.pricingMatched || false,
    pricingComplete:
      typeof liveMetrics?.pricingComplete === "boolean"
        ? liveMetrics.pricingComplete
        : localMetrics?.pricingComplete || false,
    pricingModel: liveMetrics?.pricingModel || localMetrics?.pricingModel || null,
    sourcePlatform: liveMetrics?.sourcePlatform || localMetrics?.sourcePlatform || null,
    sourcePlatforms: Array.isArray(liveMetrics?.sourcePlatforms)
      ? liveMetrics.sourcePlatforms
      : localMetrics?.sourcePlatforms || [],
    deliveryPlatformKeys: Array.isArray(liveMetrics?.platformKeys)
      ? liveMetrics.platformKeys
      : localMetrics?.deliveryPlatformKeys || [],
    contributions: Array.isArray(liveMetrics?.contributions)
      ? liveMetrics.contributions
      : localMetrics?.contributions || [],
    primaryKpi: liveMetrics?.primaryKpi || localMetrics?.primaryKpi || null,
    deliveryObserved:
      typeof liveMetrics?.deliveryObserved === "boolean"
        ? liveMetrics.deliveryObserved
        : localMetrics?.deliveryObserved || false,
    lastSyncedAt: liveMetrics?.lastSyncedAt || localMetrics?.lastSyncedAt || null,
    liveCampaigns: Array.isArray(liveMetrics?.campaigns)
      ? liveMetrics.campaigns
      : localMetrics?.liveCampaigns,
  };
}

export function formatMetricPercent(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const normalized = Math.abs(value) <= 1 ? value * 100 : value;
  return `${normalized.toFixed(normalized >= 10 ? 1 : 2)}%`;
}

/* Has any delivery been reported for this asset, by any source? */
export function hasReportedDelivery(metrics) {
  if (metrics?.deliveryObserved === true) return true;
  return [metrics?.spend, metrics?.impressions, metrics?.clicks, metrics?.videoViews, metrics?.videoStarts].some(
    (value) => typeof value === "number" && Number.isFinite(value) && value > 0
  );
}

/*
 * Platforms on which this creative is bound to run and for which the
 * advertiser has delivery that could not be linked to any creative yet. Such
 * a creative may or may not have delivered — nothing can say until the link is
 * verified — so the library must not call it "awaiting delivery".
 */
export function pendingLinkPlatformKeysFor(rowPlatformKeys = [], unresolvedEntries = []) {
  const bound = new Set((Array.isArray(rowPlatformKeys) ? rowPlatformKeys : []).map((key) => String(key || "").trim().toLowerCase()).filter(Boolean));
  const pending = new Set();
  for (const entry of Array.isArray(unresolvedEntries) ? unresolvedEntries : []) {
    const key = String(entry?.platformKey || "").trim().toLowerCase();
    if (key && bound.has(key)) pending.add(key);
  }
  return Array.from(pending).sort();
}

const PLATFORM_SHORT_LABELS = Object.freeze({ youtube: "YouTube", ctv: "CTV", display: "Display", tiktok: "TikTok" });

function platformShortLabel(key) {
  const normalized = String(key || "").trim().toLowerCase();
  return PLATFORM_SHORT_LABELS[normalized] || (normalized ? normalized.toUpperCase() : "Platform");
}

/*
 * The KPI pill. Order of authority:
 *   1. the backend's primaryKpi (leading platform contribution, its own rate);
 *   2. a rate carried on the asset record itself (ctr / vtr / engagement);
 *   3. delivery reported without a computable rate → "Delivered · no rate";
 *   4. no delivery attributed, but the advertiser has unlinked results on a
 *      platform this creative is bound to → "<Platform> link pending";
 *   5. nothing reported → "Awaiting delivery".
 */
export function getPrimaryMetricPresentation(metrics) {
  const primary = metrics?.primaryKpi;
  if (primary && typeof primary === "object" && Number.isFinite(Number(primary.value))) {
    return {
      label: String(primary.label || KPI_LABELS[primary.key] || primary.key || "KPI"),
      value: formatMetricPercent(Number(primary.value)),
      tone: "is-signal",
      platformKey: primary.platformKey || null,
    };
  }
  if (metrics?.ctr != null) {
    return {
      label: "CTR",
      value: formatMetricPercent(metrics.ctr),
      tone: "is-signal",
    };
  }
  if (metrics?.vtr != null) {
    return {
      label: "VTR",
      value: formatMetricPercent(metrics.vtr),
      tone: "is-signal",
    };
  }
  if (metrics?.engagement != null) {
    return {
      label: "ENG",
      value: formatMetricPercent(metrics.engagement),
      tone: "is-neutral",
    };
  }
  if (hasReportedDelivery(metrics)) {
    return {
      label: "Signal",
      value: "Delivered · no rate",
      tone: "is-neutral",
    };
  }
  const pendingPlatforms = Array.isArray(metrics?.pendingLinkPlatformKeys) ? metrics.pendingLinkPlatformKeys : [];
  if (pendingPlatforms.length) {
    return {
      label: "Signal",
      value: `${pendingPlatforms.map(platformShortLabel).join(" / ")} link pending`,
      tone: "is-neutral",
      pendingLink: true,
    };
  }
  return {
    label: "Signal",
    value: "Awaiting delivery",
    tone: "is-empty",
  };
}

export function hasLiveDeliverySignal(metrics) {
  if (hasReportedDelivery(metrics)) return true;
  return [metrics?.ctr, metrics?.vtr, metrics?.engagement].some(
    (value) => typeof value === "number" && Number.isFinite(value) && value > 0
  );
}

/*
 * Retained delivery whose ad name has no verified creative identity yet. It
 * belongs to no asset row, so the Library discloses it beside the table:
 * named, dated, totaled, and never attributed to a nearest-looking asset.
 */
export function normalizeUnresolvedCreativeDelivery(payload) {
  const root =
    payload?.data && typeof payload.data === "object"
      ? payload.data
      : payload && typeof payload === "object"
      ? payload
      : {};
  const entries = Array.isArray(root?.unresolvedCreativeDelivery) ? root.unresolvedCreativeDelivery : [];
  return entries
    .map((entry) => ({
      platformKey: String(entry?.platformKey || "").trim() || null,
      campaignId: entry?.campaignId ?? null,
      campaignName: String(entry?.campaignName || "").trim() || null,
      adName: String(entry?.adName || "").trim() || null,
      mappingStatus: String(entry?.mappingStatus || "").trim() || "unresolved",
      evidenceRequired: Array.isArray(entry?.evidenceRequired) ? entry.evidenceRequired.map(String) : [],
      rows: pickNumber(entry?.rows),
      days: pickNumber(entry?.days),
      firstDate: entry?.firstDate || null,
      lastDate: entry?.lastDate || null,
      spend: pickNumber(entry?.billedSpend, entry?.clientFacingSpend, entry?.spend),
      impressions: pickNumber(entry?.impressions),
      videoViews: pickNumber(entry?.videoViews),
    }))
    .filter((entry) => entry.adName);
}

/* One sentence per campaign: how many ad names await identity, and what they delivered. */
export function summarizeUnresolvedCreativeDelivery(entries = [], { formatCurrency, formatInteger } = {}) {
  const money = typeof formatCurrency === "function" ? formatCurrency : (value) => (value == null ? "—" : `$${Number(value).toFixed(0)}`);
  const count = typeof formatInteger === "function" ? formatInteger : (value) => (value == null ? "—" : String(Math.round(value)));
  const byCampaign = new Map();
  for (const entry of entries) {
    const key = `${entry.platformKey || ""}|${entry.campaignId ?? ""}`;
    if (!byCampaign.has(key)) {
      byCampaign.set(key, { platformKey: entry.platformKey, campaignId: entry.campaignId, campaignName: entry.campaignName, names: 0, spend: null, impressions: null, firstDate: null, lastDate: null });
    }
    const bucket = byCampaign.get(key);
    bucket.names += 1;
    if (entry.spend != null) bucket.spend = (bucket.spend || 0) + entry.spend;
    if (entry.impressions != null) bucket.impressions = (bucket.impressions || 0) + entry.impressions;
    if (entry.firstDate && (!bucket.firstDate || entry.firstDate < bucket.firstDate)) bucket.firstDate = entry.firstDate;
    if (entry.lastDate && (!bucket.lastDate || entry.lastDate > bucket.lastDate)) bucket.lastDate = entry.lastDate;
  }
  return Array.from(byCampaign.values()).map((bucket) => ({
    ...bucket,
    text: `${bucket.campaignName || `Campaign ${bucket.campaignId ?? "—"}`}: ${bucket.names} ${bucket.names === 1 ? "ad name" : "ad names"} · ${money(bucket.spend)} · ${count(bucket.impressions)} impressions${bucket.firstDate || bucket.lastDate ? ` · ${bucket.firstDate || "—"} → ${bucket.lastDate || "—"}` : ""}`,
  }));
}

/* Rows for the "Delivery by platform" detail: one per contribution, KPI in that platform's vocabulary. */
export function describeContributionKpi(contribution) {
  const rates = contribution?.rates || {};
  const platform = String(contribution?.platformKey || "").toLowerCase();
  const pick = (key) => (rates[key] == null ? null : { label: KPI_LABELS[key], value: formatMetricPercent(rates[key]) });
  if (platform === "youtube") return pick("videoViewRate") || pick("ctr");
  if (platform === "ctv") return pick("completionRate") || pick("ctr");
  return pick("ctr") || pick("videoViewRate") || pick("completionRate");
}
